import React, { useEffect, useState, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

/**
 * MatchCountdown — Compteur "split-flap" (panneau d'affichage de score)
 * Le chiffre tourne sur lui même façon vieux scoreboard / horloge de gare
 * lorsque la valeur change. Effet sonore "tic" mécanique optionnel à
 * chaque seconde (Web Audio API, sans fichier externe).
 *
 * Props:
 *  - target        : Date | string ISO — heure du coup d'envoi
 *  - compact       : bool — version réduite (sans labels)
 *  - accentColor   : string — couleur d'accent (chiffres) ; défaut blanc
 *  - liveLabel     : string — affiché quand le coup d'envoi est lancé
 */

function diffParts(now, target) {
  let ms = Math.max(0, target.getTime() - now.getTime());
  const d = Math.floor(ms / 86400000); ms -= d * 86400000;
  const h = Math.floor(ms / 3600000);  ms -= h * 3600000;
  const m = Math.floor(ms / 60000);    ms -= m * 60000;
  const s = Math.floor(ms / 1000);
  return { d, h, m, s, total: target.getTime() - now.getTime() };
}

const pad2 = (n) => String(n).padStart(2, "0");

// One single digit cell that flips when its value changes
function FlipDigit({ value, accentColor = "#ffffff" }) {
  return (
    <div
      className="relative inline-block w-[0.72em] h-[1em] align-baseline overflow-hidden rounded-[0.12em]"
      style={{
        background: "linear-gradient(180deg, #1a0726 0%, #0b0314 50%, #1a0726 100%)",
        boxShadow:
          "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 4px rgba(0,0,0,0.7), 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* horizontal split line, like a real flip board */}
      <div
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] z-[3] pointer-events-none"
        style={{ background: "rgba(0,0,0,0.85)", boxShadow: "0 0 4px rgba(0,0,0,0.6)" }}
      />
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          initial={{ rotateX: -90, opacity: 0 }}
          animate={{ rotateX: 0, opacity: 1 }}
          exit={{ rotateX: 90, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.7, 0.0, 0.3, 1] }}
          className="absolute inset-0 flex items-center justify-center font-bebas font-bold tabular-nums select-none"
          style={{
            color: accentColor,
            textShadow: `0 0 14px ${accentColor}55, 0 0 2px ${accentColor}aa`,
            transformOrigin: "50% 50%",
            backfaceVisibility: "hidden",
            willChange: "transform, opacity",
          }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function FlipCell({ value, label, accentColor }) {
  const [d1, d2] = pad2(value).split("");
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="flex items-baseline gap-[2px] leading-none"
        style={{ fontSize: "clamp(2.25rem, 6vw, 3.5rem)" }}
      >
        <FlipDigit value={d1} accentColor={accentColor}/>
        <FlipDigit value={d2} accentColor={accentColor}/>
      </div>
      {label && (
        <div className="font-black uppercase tracking-[0.25em] text-[9px] sm:text-[10px] text-white/60">
          {label}
        </div>
      )}
    </div>
  );
}

// Synthesize a short mechanical "tic" click via Web Audio API.
// We keep a single AudioContext, and on each tick we play a tiny exponential
// decay envelope on a high-pitch square + low-pitch thump for richness.
function useTicSound(enabled) {
  const ctxRef = useRef(null);
  const ensureCtx = () => {
    if (!enabled) return null;
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        ctxRef.current = new Ctx();
      } catch { return null; }
    }
    if (ctxRef.current?.state === "suspended") {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  };

  return () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    // High-frequency click (the "tic")
    const o1 = ctx.createOscillator();
    o1.type = "square";
    o1.frequency.setValueAtTime(2400, t0);
    o1.frequency.exponentialRampToValueAtTime(1200, t0 + 0.04);
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.0001, t0);
    g1.gain.exponentialRampToValueAtTime(0.18, t0 + 0.005);
    g1.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.06);
    o1.connect(g1).connect(ctx.destination);
    o1.start(t0); o1.stop(t0 + 0.07);
    // Low thump (the "ka")
    const o2 = ctx.createOscillator();
    o2.type = "triangle";
    o2.frequency.setValueAtTime(120, t0);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t0);
    g2.gain.exponentialRampToValueAtTime(0.12, t0 + 0.005);
    g2.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
    o2.connect(g2).connect(ctx.destination);
    o2.start(t0); o2.stop(t0 + 0.09);
  };
}

export function MatchCountdown({
  target,
  compact = false,
  accentColor = "#ffffff",
  liveLabel = "EN COURS",
  className = "",
}) {
  const targetDate = useMemo(() => {
    if (!target) return null;
    const d = target instanceof Date ? target : new Date(target);
    return isNaN(d.getTime()) ? null : d;
  }, [target]);

  const [now, setNow] = useState(() => new Date());
  const [soundOn, setSoundOn] = useState(false); // disabled by default
  const playTic = useTicSound(soundOn);
  const lastSecRef = useRef(-1);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Trigger the tic sound on every second change (when countdown is still active)
  useEffect(() => {
    if (!soundOn || !targetDate) return;
    const remain = targetDate.getTime() - now.getTime();
    if (remain <= 0) return;
    const sec = Math.floor(remain / 1000);
    if (sec !== lastSecRef.current) {
      lastSecRef.current = sec;
      playTic();
    }
  }, [now, soundOn, targetDate, playTic]);

  if (!targetDate) return null;
  const parts = diffParts(now, targetDate);

  if (parts.total <= 0) {
    return (
      <div
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase tracking-[0.3em] text-[12px] ${className}`}
        style={{
          background: "linear-gradient(135deg, #16a34a, #22c55e)",
          color: "white",
          boxShadow: "0 0 26px rgba(34,197,94,0.55)",
        }}
        data-testid="match-countdown-live"
      >
        <span className="relative w-2.5 h-2.5">
          <span className="absolute inset-0 rounded-full bg-white animate-ping opacity-80"/>
          <span className="absolute inset-0 rounded-full bg-white"/>
        </span>
        {liveLabel}
      </div>
    );
  }

  const sep = (
    <span
      aria-hidden="true"
      className="font-bebas font-bold opacity-50"
      style={{
        color: accentColor,
        fontSize: "clamp(2.25rem, 6vw, 3.5rem)",
        lineHeight: 1,
        marginTop: compact ? 0 : "-0.4em",
      }}
    >:</span>
  );

  return (
    <div className={`relative flex flex-col items-center gap-2 ${className}`}>
      <div
        className="inline-flex items-end gap-2 sm:gap-3"
        data-testid="match-countdown"
        aria-label="Compte à rebours avant le coup d'envoi"
      >
        <FlipCell value={parts.d} label={compact ? null : "Jours"}    accentColor={accentColor}/>
        {sep}
        <FlipCell value={parts.h} label={compact ? null : "Heures"}   accentColor={accentColor}/>
        {sep}
        <FlipCell value={parts.m} label={compact ? null : "Minutes"}  accentColor={accentColor}/>
        {sep}
        <FlipCell value={parts.s} label={compact ? null : "Secondes"} accentColor={accentColor}/>
      </div>

      {/* Sound toggle — placed below to avoid overlapping digits on mobile */}
      <button
        type="button"
        onClick={() => setSoundOn((v) => !v)}
        aria-label={soundOn ? "Désactiver le son du compteur" : "Activer le son du compteur"}
        title={soundOn ? "Couper le tic-tac" : "Activer le tic-tac"}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition border border-white/15 text-[9px] font-black uppercase tracking-[0.2em]"
        data-testid="countdown-sound-toggle"
      >
        {soundOn ? <Volume2 size={11}/> : <VolumeX size={11}/>}
        <span>{soundOn ? "Son" : "Muet"}</span>
      </button>
    </div>
  );
}

export default MatchCountdown;
